import React from "react";
import {
    Box,
    Grid,
    Typography,
    Paper,
    useTheme,
    useMediaQuery,
} from "@mui/material";
import PsychologyIcon from '@mui/icons-material/Psychology';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import StarIcon from '@mui/icons-material/Star';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import SpaIcon from '@mui/icons-material/Spa';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import { motion } from "framer-motion";
import LocalFloristIcon from "@mui/icons-material/LocalFlorist";

import bgImage from "../../assets/shop/col-arrow.png";
import img from "../../assets/shop/Meditation.avif";

const featureItems = [
    {
        icon: <PsychologyIcon />,
        title: "ANCIENT WISDOM",
        description: "Rooted in Vedic traditions, preserved through generations of astrological masters.",
    },
    {
        icon: <AutoAwesomeMotionIcon />,
        title: "PERSONALIZED READINGS",
        description: "Detailed birth chart analysis tailored to your unique planetary positions.",
    },
    {
        icon: <StarIcon />,
        title: "PRECISE PREDICTIONS",
        description: "Accurate forecasting based on planetary movements and cosmic alignments.",
    },
    {
        icon: <EmojiObjectsIcon />,
        title: "SPIRITUAL GUIDANCE",
        description: "Insightful counsel to navigate life's challenges with cosmic wisdom.",
    },
    {
        icon: <SpaIcon />,
        title: "PURE KNOWLEDGE",
        description: "Authentic Vedic astrology without modern distortions or shortcuts.",
    },
    {
        icon: <HistoryEduIcon />,
        title: "TIMELESS TRADITIONS",
        description: "Centuries-old techniques preserved in their original form for accurate readings.",
    },
];

const Meditation = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    // Animation variants to match Courses component
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.2 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
    };

    const cardVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
        hover: {
            scale: 1.05,
            boxShadow: "0 8px 25px rgba(109, 22, 117, 0.3)",
            transition: { duration: 0.3 }
        }
    };

    return (
        <Box 
            component={motion.div}
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.2 }}
            sx={{ 
                // mb: 2,
                p: 4,
                bgcolor: "rgba(255, 234, 223, 0.3)",
            }}
        >
            {/* Header Section */}
            <motion.div variants={itemVariants}>
                <Typography
                    variant="h4"
                    sx={{
                        textAlign: "center",
                        mt: 4,
                        fontWeight: "bold",
                        color: "#6d1675",
                        fontSize: "30px",
                        mb: 0,
                    }}
                >
                    Cosmic Wisdom & <span style={{ color: "#ffc20f" }}>Spiritual Guidance</span>
                </Typography>

                {/* Flower Divider */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                        maxWidth: 300,
                        mx: "auto",
                        my: 2,
                    }}
                >
                    <Box sx={{ flex: 1, height: 3, bgcolor: "#6d1675" }} />
                    <Box
                        sx={{
                            px: 2,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <LocalFloristIcon sx={{ fontSize: 28, color: "#ffc20f" }} />
                    </Box>
                    <Box sx={{ flex: 1, height: 3, bgcolor: "#6d1675" }} />
                </Box>
            </motion.div>

            <Box
                sx={{
                    maxWidth: "1200px",
                    mx: "auto",
                    pt: 4,
                    px: 4,
                    display: "flex",
                    alignItems:"center",
                    flexDirection: isMobile ? "column" : "row",
                    gap: 4,
                }}
            >
                {/* Left Side - Features */}
                <Grid
                    container
                    spacing={3}
                    sx={{
                        flex: 1,
                        backgroundImage: `url(${bgImage})`,
                        backgroundSize: "cover",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center",
                        borderRadius: 3,
                        p: 3,
                        bgcolor: "rgba(255, 255, 255, 0.9)",
                    }}
                >
                    {featureItems.map((item, index) => (
                        <Grid size={{xs:12, sm:4}} key={index}>
                            <motion.div
                                variants={cardVariants}
                                whileHover="hover"
                            >
                                <Paper
                                    sx={{
                                        p: 2,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        textAlign: "center",
                                        minHeight: 180,
                                        backgroundColor: "#fff",
                                        boxShadow: "0 4px 15px rgba(109, 22, 117, 0.1)",
                                        borderRadius: 3,
                                        transition: "all 0.3s ease",
                                        border: "2px solid transparent",
                                        "&:hover": {
                                            backgroundColor: "#6d1675",
                                            borderColor: "#ffc20f",
                                            "& .icon-container": {
                                                backgroundColor: "#ffc20f",
                                            },
                                            "& .icon-inner-circle": {
                                                borderColor: "#6d1675",
                                            },
                                            "& .icon": {
                                                color: "#6d1675",
                                            },
                                            "& .title": {
                                                color: "#fff",
                                            },
                                            "& .description": {
                                                color: "#ffc20f",
                                            },
                                        },
                                    }}
                                >
                                    <Box
                                        className="icon-container"
                                        sx={{
                                            background: "#6d1675",
                                            width: 60,
                                            height: 60,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: "50%",
                                            mb: 1.5,
                                            transition: "all 0.3s ease",
                                        }}
                                    >
                                        <Box
                                            className="icon-inner-circle"
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: "50%",
                                                border: "8px solid rgba(255, 194, 15, 0.2)",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                transition: "all 0.3s ease",
                                            }}
                                        >
                                            <Box
                                                className="icon"
                                                sx={{ 
                                                    color: "#ffc20f", 
                                                    fontSize: "24px",
                                                    transition: "all 0.3s ease",
                                                }}
                                            >
                                                {item.icon}
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Typography
                                        className="title"
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: "bold",
                                            color: "#6d1675",
                                            mb: 1,
                                            fontSize: "15px",
                                            transition: "all 0.3s ease",
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {item.title}
                                    </Typography>

                                    <Typography
                                        className="description"
                                        variant="body2"
                                        sx={{
                                            fontSize: "13px",
                                            color: "#666",
                                            transition: "all 0.3s ease",
                                            lineHeight: 1.4,
                                        }}
                                    >
                                        {item.description}
                                    </Typography>
                                </Paper>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>

                {/* Right Side - Image */}
                <motion.div 
                    variants={itemVariants}
                    sx={{
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Box
                        component="img"
                        src={img}
                        alt="Vedic Astrology Wisdom"
                        sx={{
                            maxWidth: "100%",
                            height: "400px",
                            borderRadius: 3,
                            boxShadow: "0 8px 25px rgba(109, 22, 117, 0.2)",
                            border: "4px solid #fff",
                            objectFit: "cover",
                        }}
                    />
                </motion.div>
            </Box>
        </Box>
    );
};

export default Meditation;